// Unified breadcrumb resolution ([NAV-BREADCRUMB-PRECEDENCE]).
//
// A single `AppBreadcrumbs` component (rendered once in MainLayout) computes the
// crumb trail for every route from two inputs:
//   1. the route-INDEPENDENT `navModel` (the same tree the sidebar renders), and
//   2. typed `route.meta.breadcrumb` metadata declared per route.
//
// This replaces the four+ ad-hoc breadcrumb implementations. Because the base
// trail comes from `navModel`, the breadcrumb and the sidebar can never disagree
// about the hierarchy portion (the single-source-of-truth guarantee).

import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import type { NavNode } from "./navModel";

/** One crumb. The last crumb in a rendered trail is the current page (no link). */
export interface BreadcrumbItem {
  label: string;
  /** Present for every crumb EXCEPT the current page. */
  to?: RouteLocationRaw;
}

/**
 * Per-route breadcrumb declaration. Resolution precedence (first match wins):
 *   1. `parentNodeId`  - the ancestor chain of that navModel node (incl. itself).
 *   2. `staticBase`    - a typed static base for non-sidebar roots (Admin).
 *   3. route-name node - the route's own page maps to a nav node.
 *   4. none            - no bar (route must be on the coverage allow-list).
 * `tail` (when present) is appended after whichever base resolved; it is
 * append-only and must not repeat the last base crumb.
 */
export interface BreadcrumbMeta {
  /**
   * Returns a node-id SUFFIX identifying the nav node this page hangs under.
   * Club-scoped ids are enough (e.g. `club:acme:meetings`); the resolver matches
   * it against the district-prefixed id in the model, so the meta author never
   * needs to know the owning district. Omit for pages that ARE a nav node.
   */
  parentNodeId?: (route: RouteLocationNormalized) => string;
  /**
   * A typed static base trail for roots that are NOT sidebar nav nodes (Admin is
   * the canonical case). Beside `parentNodeId`, it is the fallback when that node is absent.
   */
  staticBase?: (route: RouteLocationNormalized) => BreadcrumbItem[];
  /** Extra leaf crumbs appended after the base trail. */
  tail?: (route: RouteLocationNormalized) => BreadcrumbItem[];
}

declare module "vue-router" {
  interface RouteMeta {
    breadcrumb?: BreadcrumbMeta;
  }
}

// --- node lookup ----------------------------------------------------------

/** DFS returning the chain of nodes from a root down to (and including) the first match. */
function findNodePath(nodes: NavNode[], matches: (node: NavNode) => boolean): NavNode[] | null {
  for (const node of nodes) {
    if (matches(node)) return [node];
    if (node.children) {
      const sub = findNodePath(node.children, matches);
      if (sub) return [node, ...sub];
    }
  }
  return null;
}

/**
 * Params whose value the backend resolves case-insensitively, so two spellings
 * of one URL are the same page and must resolve the same trail. `Clubs.getClub`
 * looks a slug up lowercased, so `/club/ACME/meetings` renders Acme's meetings;
 * matching it case-sensitively would leave that page with no bar at all.
 */
const CASE_INSENSITIVE_PARAMS = new Set(["clubShortName"]);

function paramMatches(name: string, routeValue: unknown, nodeValue: unknown): boolean {
  const fromRoute = String(routeValue);
  const fromNode = String(nodeValue);
  if (CASE_INSENSITIVE_PARAMS.has(name)) return fromRoute.toLowerCase() === fromNode.toLowerCase();
  return fromRoute === fromNode;
}

/** Node ids embed a club slug, and a `parentNodeId` suffix is built from the raw
 *  route param, so both sides fold for the same reason `paramMatches` does. */
function matchesNodeId(node: NavNode, id: string): boolean {
  return node.id.toLowerCase() === id.toLowerCase();
}

/** Match a node by exact id or by district-agnostic suffix (`club:acme:meetings`). */
function matchesIdSuffix(node: NavNode, suffix: string): boolean {
  return matchesNodeId(node, suffix) || node.id.toLowerCase().endsWith(`:${suffix.toLowerCase()}`);
}

/** True when the node's `to` resolves to the currently-active route. */
function nodeMatchesRoute(node: NavNode, route: RouteLocationNormalized): boolean {
  const to = node.to;
  if (to === undefined) return false;
  if (typeof to === "string") return to === route.path;
  if (typeof to === "object") {
    if ("name" in to && to.name) {
      if (to.name !== route.name) return false;
      const params = to.params ?? {};
      return Object.entries(params).every(([k, v]) => paramMatches(k, route.params[k], v));
    }
    if ("path" in to && to.path) return to.path === route.path;
  }
  return false;
}

/** Nodes without a `to` (section headers) are grouping-only and not rendered as crumbs. */
function nodePathToItems(path: NavNode[]): BreadcrumbItem[] {
  return path.filter((node) => node.to !== undefined).map((node) => ({ label: node.label, to: node.to }));
}

// --- resolution -----------------------------------------------------------

/**
 * Resolve the full crumb trail for a route, or `null` when the route opts out
 * (renders no bar). Pure: all inputs are passed in, so it is unit-testable
 * without a router or store.
 */
export function resolveBreadcrumbTrail(
  navModel: NavNode[],
  route: RouteLocationNormalized,
  meta: BreadcrumbMeta | undefined,
): BreadcrumbItem[] | null {
  let base: BreadcrumbItem[] | null = null;

  // Rule 1: explicit parent nav node (single source of truth for in-tree pages).
  // Prefer an exact id match (e.g. a "My Clubs" district) over a suffix match
  // (the same district echoed under "Visiting") when both are present.
  if (meta?.parentNodeId) {
    const suffix = meta.parentNodeId(route);
    const path =
      findNodePath(navModel, (node) => matchesNodeId(node, suffix)) ??
      findNodePath(navModel, (node) => matchesIdSuffix(node, suffix));
    if (path) base = nodePathToItems(path);
  }

  // Rule 2: static base for non-sidebar roots (Admin).
  if (!base && meta?.staticBase) {
    base = meta.staticBase(route);
  }

  // Rule 3: the route's own page IS a nav node.
  if (!base) {
    const path = findNodePath(navModel, (node) => nodeMatchesRoute(node, route));
    if (path) base = nodePathToItems(path);
  }

  if (!base || base.length === 0) return null;

  const tail = meta?.tail?.(route) ?? [];
  return [...base, ...tail];
}

// --- admin meta factory ([NAV-BREADCRUMB-ADMIN-FACTORY]) --------------------
//
// Admin routes are flat sibling records with no wrapping parent, so there is no
// parent whose `meta` children could inherit. This factory declares the "Admin"
// base once; each admin record sets `meta: { breadcrumb: adminBreadcrumb([...]) }`.

const ADMIN_BASE: BreadcrumbItem[] = [{ label: "Admin", to: "/admin" }];

/**
 * Build breadcrumb meta for an admin route. `tail` is the leaf crumb(s) after
 * "Admin"; omit it for the admin home page (which is just "Admin").
 */
export function adminBreadcrumb(
  tail?: BreadcrumbItem[] | ((route: RouteLocationNormalized) => BreadcrumbItem[]),
): BreadcrumbMeta {
  return {
    staticBase: () => ADMIN_BASE,
    tail: typeof tail === "function" ? tail : tail ? () => tail : undefined,
  };
}
